import React, { useCallback, useEffect } from 'react';

import { VALIDATIONS, TOAST_TYPES, EXTENSION_TYPE } from '@shared/constants';
import { Input, ToastDialog, showToast } from '@shared/components';
import { getJWKS, onTokenUpdate, writeXLSFile } from '@utils';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';

import LoadingGif from '@assets/icons/spinner-144x144px.gif';
import SuccessIcon from '@assets/icons/done-green.svg';
import ErrorIcon from '@assets/icons/cross-red.svg';
import WarningIcon from '@assets/icons/error.svg';

import {
  ExtensionActivatorService,
  RingCentralService,
} from '@consumers/services';

import DownloadData from './download-data';
import UploadData from './upload-data';
import DuplicateDialog from './duplicate-dialog';
import ErrorDialog from './error-dialog';
import ProgressDialog from './progress-dialog';
import ErrorInfoDialog from './error-info-dialog';

const RC_ROLES = {
  SUPER_ADMIN: 'Super Admin',
};

const noop = () => {};
const paperElevation = 1;
const numericRegExp = /^\d+$/;
let accountId = null;

const defaultState = {
  uid: '',
  accountName: null,
  errorList: {},
  isLoading: false,
  isUidIncorrect: false,
  jwksToken: null,
  isErrorEncountered: false,
  showToastDialog: false,
  toastIcon: null,
  toastDialogContent: '',
  toastDialogHeading: '',
  toastActions: <></>,
  toastClose: noop,
  extensionType: EXTENSION_TYPE.LIMITED,
  isTimezoneIncluded: false,
  extensionList: [],
  isDowloadComplete: false,
  isDownloadingData: false,
  isTokenUpdated: false,
  emailValidated: false,
  manualUpdate: false,
  syncProgress: null,
  showErrorInfo: false,
  duplicateExtensionList: [],
  isRateLimited: false,
  rateLimitDelay: 0,
  isFetchingDevice: false,
  isFetchingUserRoles: false,
};

const ExtensionActivator = () => {
  const [state, setState] = React.useState(defaultState);

  //HANDLERS
  const handleChange = (event, error) => {
    let field = event.currentTarget.name;
    let value = event.currentTarget.value;
    let fieldError = error;
    if (!numericRegExp.test(value)) {
      fieldError = 'Must be a valid number';
    }
    accountId = null;
    setState(prevState => ({
      ...defaultState,
      [field]: value,
      errorList: {
        ...prevState.errorList,
        [field]: fieldError,
      },
    }));
  };

  const handleReset = () => {
    setState(defaultState);
  };

  const handleJwksResponse = (response = {}, callBack = () => {}) => {
    const { data, error, paging, token } = response;

    if (error) {
      if (Array.isArray(error) && error[0].errorCode === 'OAU-213') {
        return false;
      }
    }
    callBack(data, error, paging);
    if (token) {
      setState(prevState => ({
        ...prevState,
        jwksToken: token,
      }));
    }
    return data;
  };

  //UTILS
  const getInputAdornment = () => {
    if (state.isLoading) {
      return <img src={LoadingGif} width='26' height='26' />;
    } else if (state.isUidIncorrect) {
      return <img src={ErrorIcon} width='18' height='18' />;
    } else if (!!state.accountName && state.accountName !== 'NotFound') {
      return <img src={SuccessIcon} width='26' height='26' />;
    } else {
      return null;
    }
  };

  //DATA-FETCHER
  const fetchJwksToken = async accountUid => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await getJWKS(accountUid);
    if (error) {
      const isInvalidUid =
        Array.isArray(error) && error[0].errors[0].errorCode === 'OAU-222';
      const errorMessage = isInvalidUid
        ? `Alert: UID not located in Salesforce.`
        : `Error: ${Array.isArray(error) ? error[0].errors[0].message : error}`;

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        showToastDialog: true,
        toastDialogContent: errorMessage,
        toastDialogHeading: isInvalidUid ? 'UID Not Found' : 'Report Error...',
        toastIcon: WarningIcon,
        toastActions: (
          <div className='mt-5 mb-5'>
            <Button
              variant='contained'
              color='primary'
              className='bold-font'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  toastIcon: defaultState.toastIcon,
                  showToastDialog: defaultState.showToastDialog,
                  toastDialogHeading: defaultState.toastDialogHeading,
                  toastDialogContent: defaultState.toastDialogContent,
                  isErrorEncountered: defaultState.isErrorEncountered,
                }));
              }}
            >
              Close and Continue
            </Button>
          </div>
        ),
      }));
    } else {
      if (data) {
        setState(prevState => ({
          ...prevState,
          isLoading: true,
        }));
        onTokenUpdate(async uid => {
          const _accountUid = uid || accountUid;
          const { data, error } = await getJWKS(_accountUid);
          if (!data) {
            setState(prevState => ({
              ...prevState,
              isLoading: false,
            }));
            return false;
          }
          setState(prevState => ({
            ...prevState,
            jwksToken: data,
            isLoading: false,
            isTokenUpdated: true,
          }));
          return data;
        });
      }
      setState(prevState => ({
        ...prevState,
        jwksToken: data,
        isLoading: false,
      }));
    }
  };

  const fetchAccountName = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: false,
      accountName: ' ',
    }));
  };

  const fetchExtensionList = async (emailValidated = false) => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const query = emailValidated
      ? `type=${state.extensionType}`
      : Object.values(EXTENSION_TYPE).reduce(
          (acc, extension, index) =>
            `${acc}${index > 0 ? '&' : ''}type=${extension}`,
          ''
        );
    const response = await RingCentralService.getAllExtensions(
      state.uid,
      state.jwksToken,
      query
    );

    return handleJwksResponse(response, data => {
      setState(prevState => {
        const duplicateEmailExtensions = prevState.emailValidated
          ? []
          : getDuplicateEmailExtensions(data);
        return {
          ...prevState,
          extensionList: data,
          duplicateExtensionList: prevState.emailValidated
            ? defaultState.duplicateExtensionList
            : duplicateEmailExtensions,
          emailValidated:
            prevState.emailValidated ||
            !(duplicateEmailExtensions || []).length,
          isLoading: false,
        };
      });
    });
  };

  const fetchDeviceList = async () => {
    setState(prevState => ({
      ...prevState,
      isFetchingDevice: true,
    }));
    const response = await RingCentralService.getAllDevices(
      state.uid,
      state.jwksToken
    );

    return handleJwksResponse(response, data => {
      setState(prevState => ({
        ...prevState,
        isFetchingDevice: false,
      }));
    });
  };

  const handleErrorInfoDialog = (showErrorInfo = false) => {
    setState(prevState => ({
      ...prevState,
      showErrorInfo,
    }));
  };

  const handleDownloadConfirm = async () => {
    setState(prevState => ({
      ...prevState,
      isDownloadingData: true,
    }));
    const extensionArr = await fetchExtensionList(state.emailValidated);
    const devicesList = await fetchDeviceList();
    if (!(extensionArr && extensionArr.length)) {
      // setState(prevState => ({
      //   ...prevState,
      //   isDownloadingData: false,
      // }));
      handleErrorInfoDialog(true);
    }

    const timezoneEnabledEntries = extensionArr.filter(
      item => item.status !== 'Unassigned'
    );
    setState(prevState => ({
      ...prevState,
      syncProgress:
        prevState.isTimezoneIncluded &&
        prevState.extensionType === EXTENSION_TYPE.USER
          ? {
              label: 'DOWNLOADS REMAINING',
              totalItem: timezoneEnabledEntries.length,
              remainingItem: timezoneEnabledEntries.length,
            }
          : defaultState.syncProgress,
    }));
    if (state.isTimezoneIncluded) {
      let index = 0;
      for await (let item of extensionArr) {
        if (item.status === 'Unassigned') {
          continue;
        }
        let token = null;
        setState(prevState => {
          token = prevState.jwksToken;
          return {
            ...prevState,
          };
        });
        const response = await RingCentralService.getExtensionTimeZone(
          state.uid,
          item.id,
          token,
          rateLimit => {
            const { delay } = rateLimit;
            setState(prevState => ({
              ...prevState,
              isRateLimited: true,
              rateLimitDelay: Math.ceil(delay / 1000),
            }));
            const rateLimitDelayInterval = setInterval(() => {
              setState(prevState => {
                const rateLimitDelay =
                  prevState.rateLimitDelay > 0
                    ? prevState.rateLimitDelay - 1
                    : prevState.rateLimitDelay;
                return { ...prevState, rateLimitDelay };
              });
            }, 1000);

            setTimeout(() => {
              clearInterval(rateLimitDelayInterval);
              setState(prevState => ({
                ...prevState,
                isRateLimited: false,
              }));
            }, delay);
          }
        );
        handleJwksResponse(response, data => {
          item.timeZone = data?.regionalSettings?.timezone
            ? data.regionalSettings.timezone.name
            : 'Unknown';
          setState(prevState => ({
            ...prevState,
            syncProgress:
              prevState.extensionType === EXTENSION_TYPE.USER
                ? {
                    ...prevState.syncProgress,
                    remainingItem: timezoneEnabledEntries.length - (index + 1),
                  }
                : defaultState.syncProgress,
          }));
          index++;
        });
      }
    }
    const dataToDownload = await getdataToDownload(extensionArr, devicesList);
    if (dataToDownload.length) {
      await writeXLSFile(
        `ExtensionActivator_${state.extensionType}Extension.xlsx`,
        'sheet',
        dataToDownload,
        {
          bookType: 'xlsx',
          raw: false,
        }
      );
    }
    setState(prevState => ({
      ...prevState,
      isDownloadingData: false,
      isDowloadComplete: true,
      syncProgress: null,
    }));
  };

  const getdataToDownload = async (extensionList = [], devicesList = []) => {
    let extensions = [...(extensionList || [])];
    let phones = [...(devicesList || [])];
    let preparedData = [];

    phones = phones.filter(phone => !!phone.extension);
    let i = 0;
    do {
      let id = extensions[i]?.id ? extensions[i].id : '';
      let phoneObj = phones.find(item => item.extension.id === id);
      let userObj = extensions[i];
      let data = {
        MailboxID: id,
        SendWelcomeEmail: id ? false : '',
        Status: userObj?.status ? userObj.status : '',
        Extension: userObj?.extensionNumber || '',
        FirstName: userObj?.contact ? userObj.contact.firstName : '',
        LastName: userObj?.contact ? userObj.contact.lastName : '',
        Email: userObj?.contact ? userObj.contact.email : '',
        Timezone: userObj?.timeZone ? userObj.timeZone : '',
        Password: '',
        Pin: '',
        PhoneID: phoneObj ? phoneObj.id : '',
        Model: phoneObj && phoneObj.model ? phoneObj.model.name : '',
        DeviceName: phoneObj ? phoneObj.name : '',
        Serial: phoneObj ? phoneObj.serial : '',
        Number:
          phoneObj && phoneObj.phoneLines[0]
            ? phoneObj.phoneLines[0].phoneInfo.phoneNumber
            : '',
        CustomerName: '',
        Street: '',
        Street2: '',
        City: '',
        State: '',
        ZipCode: '',
        Country: userObj ? 'US' : '',
        Site: userObj?.site ? userObj.site.name : '',
      };
      preparedData.push(data);
      i += 1;
    } while (i < extensions.length);
    return preparedData;
  };

  const getDuplicateEmailExtensions = data => {
    const duplicateEntries = (data || [])
      .filter(
        (extension, index) =>
          extension?.contact?.email &&
          index !==
            data.findIndex(
              item =>
                (item?.contact?.email || '').toLowerCase() ===
                (extension?.contact?.email || '').toLowerCase()
            )
      )
      .map(extension => (extension?.contact?.email || '').toLowerCase());

    return (data || []).filter(
      extension =>
        extension?.contact?.email &&
        duplicateEntries.includes(
          (extension?.contact?.email || '').toLowerCase()
        )
    );
  };

  const handleSyncExtensions = async (extensions = []) => {
    setState(prevState => ({
      ...prevState,
      duplicateExtensionList: defaultState.duplicateExtensionList,
      manualUpdate: false,
      syncProgress: {
        label: 'EMAILS REMAINING',
        totalItem: extensions.length,
        remainingItem: extensions.length,
      },
    }));
    for await (let [index, extension] of extensions.entries()) {
      const response = await RingCentralService.updateExtension(
        { contact: { email: extension.contact.email } },
        accountId,
        extension.id,
        state.jwksToken
      );
      let toastDialog = {};
      if (index + 1 === extensions.length || response.error) {
        toastDialog = {
          ...toastDialog,
          showToastDialog: true,
          isErrorEncountered: !!response.error,
          toastDialogHeading: !!response.error
            ? 'Error Encountered...'
            : 'UPDATE COMPLETE',
          toastDialogContent: !!response.error ? (
            `Error: ${
              Array.isArray(response.error)
                ? response.error[0].message
                : response.error
            }`
          ) : (
            <>
              Success! <br /> {index + 1} email(s) have been updated.
            </>
          ),
          syncProgress: null,
          toastActions: !response.error ? (
            <div className='mt-2 mb-5'>
              <Button
                variant='contained'
                color='primary'
                className='bold-font'
                onClick={() => {
                  setState(prevState => ({
                    ...prevState,
                    showToastDialog: defaultState.showToastDialog,
                    toastDialogHeading: defaultState.toastDialogHeading,
                    toastDialogContent: defaultState.toastDialogContent,
                    isErrorEncountered: defaultState.isErrorEncountered,
                    emailValidated: true,
                  }));
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <></>
          ),
          toastClose: () => {
            if (!!response.error) {
              handleReset();
            } else {
              setState(prevState => ({
                ...prevState,
                emailValidated: true,
              }));
            }
          },
        };
      }
      setState(prevState => ({
        ...prevState,
        syncProgress: {
          ...prevState.syncProgress,
          remainingItem: extensions.length - (index + 1),
        },
        ...toastDialog,
      }));
      if (!!response.error) {
        break;
      }
    }
  };

  const extensionsFilterOutByRole = async (extensions = [], role = []) => {
    setState(prevState => ({
      ...prevState,
      isFetchingUserRoles: true,
    }));
    const filteredExtensions = [];
    for await (let extension of extensions) {
      let token = null;
      setState(prevState => {
        token = prevState.jwksToken;
        return {
          ...prevState,
        };
      });
      const response = await RingCentralService.getRCAssignedRole(
        state.uid,
        extension.id,
        token
      );
      handleJwksResponse(response, data => {
        if ((data[0] && data[0].displayName) !== RC_ROLES.SUPER_ADMIN)
          filteredExtensions.push(extension);
      });
    }
    setState(prevState => ({
      ...prevState,
      isFetchingUserRoles: false,
    }));
    return filteredExtensions;
  };

  const handleAutofixDuplicateEmails = useCallback(async () => {
    const duplicateExtensions = await extensionsFilterOutByRole(
      state.duplicateExtensionList
    );
    if (!duplicateExtensions.length) {
      return showToast(
        'All duplicate emails belong to Super Admins. Please update manually.',
        TOAST_TYPES.ERROR
      );
    }
    const fixedExtensions = duplicateExtensions.map(extension => {
      const emailArr = (extension.contact?.email || '').split('@');
      const fixedEmail = `${emailArr[0]}${extension.extensionNumber}@${emailArr[1]}`;
      return {
        ...extension,
        contact: {
          ...extension.contact,
          email: fixedEmail,
        },
      };
    });
    handleSyncExtensions(fixedExtensions);
  }, [state.duplicateExtensionList]);

  const handelManualUpdate = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      manualUpdate: true,
    }));
  }, [state.duplicateExtensionList]);

  const validateDuplicateAfterChange = (extensions = []) => {
    const filteredExtensions = state.extensionList.filter(
      extension => !extensions.some(item => item.id === extension.id)
    );
    const extesnionWithChangedEmail = [...filteredExtensions, ...extensions];
    const duplicateExt = getDuplicateEmailExtensions(extesnionWithChangedEmail);
    return [
      ...extensions,
      ...duplicateExt.filter(
        ext => !extensions.some(item => item.id === ext.id)
      ),
    ];
  };

  //USE-EFFECTS
  React.useEffect(() => {
    if (!!state.jwksToken && state.emailValidated) {
      fetchAccountName();
    }
  }, [state.jwksToken, state.emailValidated]);

  React.useEffect(() => {
    if (!!state.jwksToken && !state.isTokenUpdated) {
      !state.emailValidated && fetchExtensionList(state.emailValidated);
    }
  }, [state.jwksToken, state.emailValidated]);

  //JSX
  return (
    <>
      <div>
        <Paper className='p-6 mr-10 ml-10' elevation={paperElevation}>
          <div>
            <Typography variant='h5' className='medium-font mb-2'>
              Extension Activator
            </Typography>
            <Typography variant='caption' className='color-text-secondary'>
              This tool lets you activate and/or update multiple limited
              extensions or user extensions on an account with a simple Excel
              file.
            </Typography>
          </div>
          <Typography variant='body1' className='bold-font mt-4'>
            Enter the UID
          </Typography>
          <div className='d-flex f-align-center'>
            <div className='w-25'>
              <Input
                onKeyDown={event => {
                  if (parseInt(event.keyCode) === 13) {
                    if (
                      !event.target.value ||
                      accountId === event.target.value ||
                      !!state.errorList?.uid ||
                      state.jwksToken
                    ) {
                      return;
                    }
                    fetchJwksToken(event.target.value);
                    accountId = event.target.value;
                  }
                }}
                type='text'
                placeholder='Target Account (UID)'
                disabled={state.isDowloadComplete}
                name='uid'
                fullWidth
                className='mb-4'
                value={
                  !!state.accountName && state.accountName != 'NotFound'
                    ? `${state.uid} - ${state.accountName}`
                    : `${state.uid}`
                }
                onChange={(event, error) => {
                  handleChange(event, error);
                }}
                hasError={!!state.errorList?.uid}
                helperText={state.errorList?.uid}
                onBlur={event => {
                  if (
                    !event.currentTarget.value ||
                    accountId === event.currentTarget.value ||
                    !!state.errorList?.uid ||
                    state.jwksToken
                  ) {
                    return;
                  }
                  fetchJwksToken(event.currentTarget.value);
                  accountId = event.currentTarget.value;
                }}
                validations={{
                  [VALIDATIONS.MAX_LENGTH]: 19,
                  [VALIDATIONS.REQUIRED]: true,
                }}
                InputProps={{
                  endAdornment: getInputAdornment(),
                }}
              />
            </div>
            {!!state.accountName && (
              <RadioGroup
                className='d-flex f-align-center flex-row ml-10'
                aria-label='extensionType'
                name='extensionType'
                value={state.extensionType}
              >
                <Radio
                  size='small'
                  color='primary'
                  checked={state.extensionType === EXTENSION_TYPE.LIMITED}
                  disabled={state.isDowloadComplete}
                  onChange={() => {
                    setState(prevState => ({
                      ...prevState,
                      extensionType: EXTENSION_TYPE.LIMITED,
                    }));
                  }}
                />
                <Typography variant='body1' className='mr-5'>
                  Limited Extensions
                </Typography>
                <Radio
                  size='small'
                  color='primary'
                  checked={state.extensionType === EXTENSION_TYPE.USER}
                  disabled={state.isDowloadComplete}
                  onChange={() => {
                    setState(prevState => ({
                      ...prevState,
                      extensionType: EXTENSION_TYPE.USER,
                    }));
                  }}
                />
                <Typography variant='body1'>User Extensions</Typography>
              </RadioGroup>
            )}
          </div>
          {state.accountName && (
            <div>
              {state.extensionType === EXTENSION_TYPE.LIMITED ? (
                <Typography variant='body1' className='bold-font mt-5 mb-2'>
                  RC Limited Extension Activator
                </Typography>
              ) : (
                <Typography variant='body1' className='bold-font mt-5 mb-2'>
                  RC User Extension Activator
                </Typography>
              )}
              {state.extensionType === EXTENSION_TYPE.LIMITED && (
                <Typography variant='body2'>
                  - Do not enter a LAST NAME <br />
                  - Remember to put in a password and pin or it will break the
                  account <br />- If you don't add a timezone, it will be
                  incorrect and default to EST
                </Typography>
              )}
            </div>
          )}
        </Paper>
        {state.accountName && state.extensionType && (
          <DownloadData
            syncProgress={state.syncProgress}
            onDownloadConfirm={handleDownloadConfirm}
            disabled={state.isDowloadComplete}
            extensionType={state.extensionType}
            isTimezoneIncluded={state.isTimezoneIncluded}
            isDownloadingData={state.isDownloadingData}
            onChange={value => {
              setState(prevState => ({
                ...prevState,
                isTimezoneIncluded: value,
              }));
            }}
          />
        )}

        {state.isDowloadComplete && (
          <UploadData
            accountId={state.uid}
            handleJwksResponse={handleJwksResponse}
            getJWKSToken={() => {
              let token = null;
              setState(prevState => {
                token = prevState.jwksToken;
                return {
                  ...prevState,
                };
              });
              return token;
            }}
            onReset={handleReset}
            extensionType={state.extensionType}
          />
        )}
      </div>
      {state.showToastDialog && (
        <ToastDialog
          open={state.showToastDialog}
          heading={state.toastDialogHeading}
          contentText={state.toastDialogContent}
          isError={state.isErrorEncountered}
          toastActions={state.toastActions}
          toastIcon={state.toastIcon}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              showToastDialog: defaultState.showToastDialog,
              toastDialogHeading: defaultState.toastDialogHeading,
              toastDialogContent: defaultState.toastDialogContent,
              isErrorEncountered: defaultState.isErrorEncountered,
              toastIcon: defaultState.toastIcon,
            }));
            state.toastClose();
          }}
        />
      )}
      {!!state.duplicateExtensionList.length && (
        <DuplicateDialog
          open={!!state.duplicateExtensionList.length}
          heading='Duplicate Email(s) Found On Account'
          subHeading={`${state.duplicateExtensionList.length} duplicate(s) found`}
          content='Select Auto Fix or Manual Update to correct duplicate emails now.'
          autofixTooltip='This button automatically takes the current extension number and adds that in front of the @ symbol in the email address.'
          manualUpdateTooltip='This button allows you to manually correct any duplicate emails found.'
          onAutofix={() => handleAutofixDuplicateEmails()}
          onManualUpdate={() => handelManualUpdate()}
          isFetchingUserRoles={state.isFetchingUserRoles}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              emailValidated: true,
              duplicateExtensionList: defaultState.duplicateExtensionList,
            }));
          }}
        />
      )}
      {state.manualUpdate && (
        <ErrorDialog
          open={state.manualUpdate}
          heading={`${state.duplicateExtensionList.length} Duplicate Email(s) Found`}
          data={state.duplicateExtensionList}
          onSync={data => {
            const extensions = validateDuplicateAfterChange(data);
            const hasDuplicate = !!getDuplicateEmailExtensions(extensions)
              .length;
            hasDuplicate
              ? setState(prevState => {
                  const extensionIds = extensions.map(item => item.id);
                  const updatedExtension = prevState.duplicateExtensionList.filter(
                    extension => !extensionIds.includes(extension.id)
                  );
                  return {
                    ...prevState,
                    duplicateExtensionList: [
                      ...updatedExtension,
                      ...extensions,
                    ],
                  };
                })
              : handleSyncExtensions(data);
          }}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              manualUpdate: false,
            }));
          }}
        />
      )}
      {!!state.syncProgress && (
        <ProgressDialog
          open={!!state.syncProgress}
          label={state.syncProgress?.label}
          totalItem={state.syncProgress?.totalItem}
          remainingItem={state.syncProgress?.remainingItem}
          isRateLimited={state.isRateLimited}
          rateLimitDelay={state.rateLimitDelay}
        />
      )}
      {!!state.showErrorInfo && (
        <ErrorInfoDialog
          open={state.showErrorInfo}
          accountId={accountId}
          handleClose={() => handleErrorInfoDialog(false)}
        />
      )}
    </>
  );
};
export default ExtensionActivator;
